import type React from 'react';
import { useCallback } from 'react';
import { motion } from 'framer-motion';
import { GitBranch, Calendar, BarChart3, Users, FileText, Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { fadeUp, Reveal } from './animations';

/* ── Types ───────────────────────────────────── */

type HeroFeature = {
  icon: 'canvas' | 'timeline' | 'pert' | 'skills' | 'adrs' | 'resources';
  title: string;
  desc: string;
};

type FeatureCategory = {
  title: string;
  items: string[];
};

type ShowcaseContent = {
  heading: string;
  sub: string;
  heroes: [HeroFeature, HeroFeature, HeroFeature, HeroFeature, HeroFeature, HeroFeature];
  cta: { canvas: string; board: string; hint: string };
  wallHeading: string;
  categories: FeatureCategory[];
};

/* ── Content ─────────────────────────────────── */

const content: Record<'de' | 'en', ShowcaseContent> = {
  de: {
    heading: 'Features',
    sub: 'Canvas, Timeline, Schätzungen und Skill-Matrix — alles hängt zusammen. Änderungen wirken sofort überall.',
    heroes: [
      {
        icon: 'canvas',
        title: 'Canvas',
        desc: 'Freifläche für Tech-Trees, Diagramme und Projektstruktur. 9 Entity-Typen, Layer-System, 5 Connector-Algorithmen, 2000+ Sprites, Undo/Redo.',
      },
      {
        icon: 'timeline',
        title: 'Gantt-Timeline',
        desc: 'Roadmap mit 4 Detail-Stufen, Drag-and-Drop, stufenlosem Zoom und kontextsensitiven Menüs. Stakeholder sehen wann was fertig wird.',
      },
      {
        icon: 'pert',
        title: 'PERT-Schätzungen',
        desc: 'Drei-Punkt-Methode nach Robert C. Martin. Junioren und Senioren schätzen einzeln. Risiko ist kein Gefühl — es ist eine Zahl.',
      },
      {
        icon: 'skills',
        title: 'Skills & Squad-Forming',
        desc: 'Wer kann was, wo sind Lücken. Team-Zusammensetzung mit sofortiger Auswirkungsanalyse. Doppelbuchungs-Erkennung boardübergreifend.',
      },
      {
        icon: 'adrs',
        title: 'Architecture Decision Records',
        desc: 'Warum wurde das so gebaut? Kontext, Entscheidung, Konsequenzen — direkt im Planungstool. Mit Canvas-Integration, Status-Workflow und Relationen.',
      },
      {
        icon: 'resources',
        title: 'Ressourcen & Infrastrukturkosten',
        desc: 'Server-Kosten an Canvas-Elemente heften. Hetzner-Produktkatalog, automatische Aggregation in Gruppen und Canvas-Übersicht mit Gesamtkosten.',
      },
    ],
    cta: { canvas: 'Canvas erstellen', board: 'Board erstellen', hint: 'Kein Signup. Einfach loslegen.' },
    wallHeading: 'Im Detail',
    categories: [
      {
        title: 'Board & Planung',
        items: ['Multi-Board-Architektur', 'Flexible Spalten & Workflows', 'Task-Pool mit Fuzzy-Suche', 'Bulk-Operationen', 'Kategorien mit Farbcodes', 'Rich-Text Beschreibungen', 'Business-Impact pro Task', 'Requirements (Hard & Soft)', 'Architecture Decision Records (ADRs)'],
      },
      {
        title: 'Schätzung & Risiko',
        items: ['PERT (O/M/P)', 'Pro-Person-Schätzung', 'Collaboration Penalty', 'Multitasking-Erkennung', 'Best-Case Overlay', 'Risiko-Farbcodierung', 'Konfidenzintervalle', 'Beitrags-Analyse pro Person'],
      },
      {
        title: 'Canvas',
        items: ['9 Entity-Typen', 'Layer & Gruppen', '5 Connector-Routings', '2000+ Sprites', 'Undo/Redo', 'Dependency Explorer', 'Copy as Image', 'Inline-Editing'],
      },
      {
        title: 'Timeline',
        items: ['4 Detail-Level', 'Multi-Row Header', 'Box-Select & Marching Ants', 'Drag-and-Drop & Resize', 'Kontextmenüs', 'Keyboard-Shortcuts', 'View-Settings', 'Snap-Indicator'],
      },
      {
        title: 'Team & Skills',
        items: ['Skill-Matrix (3 Level)', 'Skill-Anforderungen (DSL)', 'Gap-Analyse', 'Team-Coverage', 'Doppelbuchungs-Erkennung', 'Konsequenzen pro Task', 'Verschiedene Perspektiven'],
      },
      {
        title: 'Ressourcen & Kosten',
        items: ['Hetzner-Produktkatalog', 'Typisierte Felder (Währung/Zahl/Text)', 'Aggregation (Summe/Avg/Min/Max)', 'Drag-&-Drop auf Canvas', 'Canvas-Übersicht mit Tabelle', 'Gruppen-Aggregation', 'Scope (Global/Board/Canvas)'],
      },
    ],
  },
  en: {
    heading: 'One tool. Every detail.',
    sub: 'Canvas, Timeline, Estimates and Skill Matrix — everything is connected. Changes take effect everywhere, instantly.',
    heroes: [
      {
        icon: 'canvas',
        title: 'Canvas',
        desc: 'Freeform space for tech trees, diagrams and project structure. 9 entity types, layer system, 5 connector algorithms, 2000+ sprites, undo/redo.',
      },
      {
        icon: 'timeline',
        title: 'Gantt Timeline',
        desc: 'Roadmap with 4 detail levels, drag-and-drop, smooth zoom and context menus. Stakeholders see when things ship.',
      },
      {
        icon: 'pert',
        title: 'PERT Estimates',
        desc: 'Three-point method inspired by Robert C. Martin. Juniors and seniors estimate individually. Risk is not a feeling — it\'s a number.',
      },
      {
        icon: 'skills',
        title: 'Skills & Squad Forming',
        desc: 'Who can do what, where are the gaps. Team composition with instant impact analysis. Double-booking detection across boards.',
      },
      {
        icon: 'adrs',
        title: 'Architecture Decision Records',
        desc: 'Why was it built this way? Context, decision, consequences — right in the planning tool. With canvas integration, status workflow and relations.',
      },
      {
        icon: 'resources',
        title: 'Resources & Infrastructure Costs',
        desc: 'Attach server costs to canvas elements. Hetzner product catalog, automatic aggregation in groups, and canvas overview with total costs.',
      },
    ],
    cta: { canvas: 'Create Canvas', board: 'Create Board', hint: 'No signup. Just create.' },
    wallHeading: 'Every detail, intentional.',
    categories: [
      {
        title: 'Board & Planning',
        items: ['Multi-board architecture', 'Flexible columns & workflows', 'Task pool with fuzzy search', 'Bulk operations', 'Color-coded categories', 'Rich-text descriptions', 'Business impact per task', 'Requirements (hard & soft)', 'Architecture Decision Records (ADRs)'],
      },
      {
        title: 'Estimation & Risk',
        items: ['PERT (O/M/P)', 'Per-person estimation', 'Collaboration penalty', 'Multitasking detection', 'Best-case overlay', 'Risk color coding', 'Confidence intervals', 'Per-person contribution analysis'],
      },
      {
        title: 'Canvas',
        items: ['9 entity types', 'Layers & groups', '5 connector routings', '2000+ sprites', 'Undo/redo', 'Dependency explorer', 'Copy as image', 'Inline editing'],
      },
      {
        title: 'Timeline',
        items: ['4 detail levels', 'Multi-row header', 'Box-select & marching ants', 'Drag-and-drop & resize', 'Context menus', 'Keyboard shortcuts', 'View settings', 'Snap indicator'],
      },
      {
        title: 'Team & Skills',
        items: ['Skill matrix (3 levels)', 'Skill requirements (DSL)', 'Gap analysis', 'Team coverage', 'Double-booking detection', 'Consequences per task', 'Multiple perspectives'],
      },
      {
        title: 'Resources & Costs',
        items: ['Hetzner product catalog', 'Typed fields (currency/number/text)', 'Aggregation (sum/avg/min/max)', 'Drag-and-drop to canvas', 'Canvas overview with table', 'Group aggregation', 'Scope (global/board/canvas)'],
      },
    ],
  },
};

/* ── Icon map ────────────────────────────────── */

const heroIcons = {
  canvas: GitBranch,
  timeline: Calendar,
  pert: BarChart3,
  skills: Users,
  adrs: FileText,
  resources: Package,
} as const;

/* ── Component ───────────────────────────────── */

export function LandingFeatureShowcase({ featuresRef, onCreateCanvas, onCreateBoard }: { featuresRef?: React.RefObject<HTMLElement | null>; onCreateCanvas?: () => void; onCreateBoard?: () => void }) {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'de';
  const c = content[lang];

  const handleCardEnter = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.currentTarget.style.borderColor = 'var(--landing-accent)';
    e.currentTarget.style.boxShadow = '0 0 40px -8px hsla(235,50%,60%,0.25)';
  }, []);

  const handleCardLeave = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.currentTarget.style.borderColor = 'var(--landing-border)';
    e.currentTarget.style.boxShadow = 'none';
  }, []);

  return (
    <>
      {/* ── Hero Features ── */}
      <section ref={featuresRef} style={{ padding: '120px 24px 64px', maxWidth: 1080, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
        <Reveal>
          <motion.h2 className="landing-section-heading" variants={fadeUp} custom={0}>
            {c.heading}
          </motion.h2>
          <motion.p className="landing-section-sub" variants={fadeUp} custom={1}>
            {c.sub}
          </motion.p>
        </Reveal>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20 }}>
          {c.heroes.map((hero, idx) => {
            const Icon = heroIcons[hero.icon];
            const isLast = idx === c.heroes.length - 1 && c.heroes.length % 2 === 1;
            return (
              <div
                key={hero.icon}
                style={{
                  background: 'var(--landing-surface)',
                  border: '1px solid var(--landing-border)',
                  borderRadius: 16,
                  padding: 28,
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'border-color 0.25s, box-shadow 0.25s',
                  cursor: 'default',
                  ...(isLast ? { gridColumn: '1 / -1' } : {}),
                }}
                onMouseEnter={handleCardEnter}
                onMouseLeave={handleCardLeave}
              >
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'hsla(235,50%,60%,0.12)', marginBottom: 16,
                }}>
                  <Icon size={20} style={{ color: 'var(--landing-accent)' }} />
                </div>
                <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--landing-text-1)', margin: '0 0 8px' }}>
                  {hero.title}
                </h3>
                <p style={{ fontSize: 14, color: 'var(--landing-text-2)', margin: 0, lineHeight: 1.65 }}>
                  {hero.desc}
                </p>
              </div>
            );
          })}
        </div>

        {onCreateCanvas && onCreateBoard && (
          <div style={{ textAlign: 'center', marginTop: 40 }}>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginBottom: 12 }}>
              <button className="landing-btn-primary" onClick={onCreateCanvas}>{c.cta.canvas}</button>
              <button className="landing-btn-primary" onClick={onCreateBoard}>{c.cta.board}</button>
            </div>
            <p style={{ fontSize: 13, color: 'var(--landing-text-3)', margin: 0 }}>{c.cta.hint}</p>
          </div>
        )}
      </section>

      {/* ── Feature Wall ── */}
      <section style={{ padding: '0 24px 96px', maxWidth: 1080, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
        <Reveal>
          <motion.h2 className="landing-section-heading" variants={fadeUp} custom={0}>
            {c.wallHeading}
          </motion.h2>
        </Reveal>
        </div>

        <div className="landing-detail-grid">
          {c.categories.map((cat) => (
            <div
              key={cat.title}
              style={{
                background: 'var(--landing-surface)',
                border: '1px solid var(--landing-border)',
                borderRadius: 12,
                padding: 24,
              }}
            >
              <h3 style={{ fontSize: 13, fontWeight: 600, color: 'var(--landing-accent)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 16px' }}>
                {cat.title}
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {cat.items.map((item) => (
                  <li key={item} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--landing-text-2)' }}>
                    <span style={{ width: 4, height: 4, borderRadius: 2, background: 'var(--landing-text-4)', flexShrink: 0 }} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
